import {
	createContext,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { type Edition } from '@/components/signup/utils/products';
import { ufoEventGetUserProfile } from '@/components/signup/utils/ufo-events';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import type {
	CcpTransactionAccountData,
	ServerSideRequestStatus,
	UserProfile,
	UserProfileResponse,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import type { GrowthUnifiedProfile } from '@/libraries/growth-unified-profile-client/unified-profile.schema';
import { Cookie } from '@/utils/cookie.ts';
import { type LoggedInUser, useLoggedInUser } from '@/utils/logged-in-user-provider';

import { Product } from './helpers/products';
import { useProductIntent } from './helpers/use-product-intent';

type Profile = Partial<UserProfileResponse> & { requestInProgress: boolean };
export type User = Omit<LoggedInUser, 'isUserLoggedIn'> & {
	isUserLoggedIn?: boolean;
};

export type UserContextType = {
	edition?: Edition;
	loggedOutProfile?: Partial<GrowthUnifiedProfile>;
	loggedInProfile?: Partial<GrowthUnifiedProfile>;
	product?: Product;
	profile: Profile;
	referrer?: string;
	user: User;
	requestClientUserProfile: () => void;
	transactionAccounts: Array<CcpTransactionAccountData>;
	utmCampaign?: string | null;
};

export const UserContext = createContext<UserContextType | null>(null);

export const UserContextProvider = ({
	children,
	location = 'https://www.atlassian.com/',
	referrer,
	meResponse,
	meRequest,
	userProfileResponse,
	userProfileRequest,
	loggedOutContext,
	loggedOutContextStatus,
	loggedInContext,
	loggedInContextStatus,
	transactionAccounts = [],
	utmCampaign = null,
}: PropsWithChildren<{
	location?: string;
	referrer?: string;
	meResponse?: UserProfile;
	meRequest?: ServerSideRequestStatus;
	userProfileResponse?: UserProfileResponse;
	userProfileRequest?: ServerSideRequestStatus;
	loggedOutContext?: Partial<GrowthUnifiedProfile>;
	loggedOutContextStatus?: ServerSideRequestStatus;
	loggedInContext?: Partial<GrowthUnifiedProfile>;
	loggedInContextStatus?: ServerSideRequestStatus;
	transactionAccounts?: Array<CcpTransactionAccountData>;
	utmCampaign?: string | null;
}>) => {
	const meResponseProvided = useMemo(
		() => meRequest && meRequest.requested && meRequest.success,
		[meRequest],
	);
	const userProfileProvided = useMemo(
		() => userProfileRequest && userProfileRequest.requested && userProfileRequest.success,
		[userProfileRequest],
	);
	const loggedOutProfile = useMemo(
		() =>
			loggedOutContextStatus?.requested && loggedOutContextStatus?.success
				? loggedOutContext
				: undefined,
		[loggedOutContext, loggedOutContextStatus],
	);
	const loggedInProfile = useMemo(
		() =>
			loggedInContextStatus?.requested && loggedInContextStatus?.success
				? loggedInContext
				: undefined,
		[loggedInContext, loggedInContextStatus],
	);
	const clientUserProfileRequested = useRef<'notRequested' | 'inProgress' | 'complete'>(
		userProfileRequest?.requested && userProfileRequest?.success ? 'complete' : 'notRequested',
	);

	// If logged in user cookie isn't present, then we don't need to make a call to stargate.
	// This cookie is set by bxp-gateway to determine if the user is logged in or not.
	// However, when static fallback is served from edge cache, setHeaders() never runs and login_user_detected is not set.
	// As a fallback, check for cloud.session.token directly (which is the source of truth for login state).
	const loginUserDetectedCookie = Cookie.readCookie('login_user_detected') === 'true';
	const cloudSessionToken =
		Cookie.readCookie('cloud.session.token') || Cookie.readCookie('cloud.session.token.stg');
	const loginUserDetected = loginUserDetectedCookie || cloudSessionToken !== null;

	const [user, setUser] = useState<User>({
		...meResponse,
		isUserLoggedIn: meResponseProvided ? !!meResponse : undefined,
		requestInProgress: !meResponseProvided,
	});

	const [transactionAccountsState] =
		useState<Array<CcpTransactionAccountData>>(transactionAccounts);

	const [profile, setProfile] = useState<Profile>({
		...userProfileResponse,
		requestInProgress:
			!userProfileProvided &&
			!!userProfileRequest &&
			userProfileRequest.requested &&
			loginUserDetected,
	});

	const stargateClient = useBxpWebStargateClient();
	const { product, edition } = useProductIntent(location);
	const clientSideUser = useLoggedInUser();

	const fetchUserProfile = useCallback(() => {
		if (!loginUserDetected || clientUserProfileRequested.current !== 'notRequested') {
			return;
		}
		clientUserProfileRequested.current = 'inProgress';
		setProfile((profile) => ({ ...profile, requestInProgress: true }));

		ufoEventGetUserProfile.start();
		stargateClient
			.getUserProfile({ productName: product?.name }) // todo decide if it might be better to always send undefined
			.then((userProfile) => {
				setProfile({ ...userProfile, requestInProgress: false });
				ufoEventGetUserProfile.success({
					customData: {
						cohorts: userProfile.cohorts,
						reactComponent: 'UserContextProvider',
						analyticsEvent: 'succeeded',
						productName: product?.name,
					},
				});
			})
			.catch((error) => {
				console.error('Failed to get user profile', error);
				ufoEventGetUserProfile.failure({
					customData: {
						reactComponent: 'UserContextProvider',
						analyticsEvent: 'errored',
						productName: product?.name,
					},
					error,
				});
				setProfile({ requestInProgress: false });
			})
			.finally(() => {
				clientUserProfileRequested.current = 'complete';
			});
	}, [stargateClient, product, loginUserDetected]);

	useEffect(() => {
		if (meResponseProvided || clientSideUser.requestInProgress) {
			return;
		}
		setUser(clientSideUser);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clientSideUser.requestInProgress, meResponseProvided]); // don't add clientSideUser to the dependency array to avoid infinite loop

	useEffect(() => {
		if (userProfileProvided || !userProfileRequest?.requested) {
			return;
		}
		fetchUserProfile();
	}, [userProfileProvided, fetchUserProfile, userProfileRequest?.requested]);

	return (
		<UserContext.Provider
			value={{
				edition,
				loggedOutProfile,
				loggedInProfile,
				product,
				profile,
				referrer,
				user,
				transactionAccounts: transactionAccountsState,
				requestClientUserProfile: () => {
					if (
						clientUserProfileRequested.current === 'notRequested' &&
						!userProfileProvided &&
						!profile.requestInProgress
					) {
						fetchUserProfile();
					}
				},
				utmCampaign,
			}}
		>
			{children}
		</UserContext.Provider>
	);
};

export const useUserContext = (): UserContextType => {
	const context = useContext(UserContext);

	if (!context) {
		throw new Error('useUserContext must be used within a UserContextProvider');
	}

	context.requestClientUserProfile();

	return context;
};
